import React, { useState, useCallback, createContext, useEffect, lazy, Suspense, useRef } from 'react';
import type { User } from './types';
import ErrorBoundary from './components/ErrorBoundary';
import { Header } from './components/Header';
import Sidebar from './components/Sidebar';
import ContextMenu from './components/ContextMenu';
import Toast, { type ToastItem } from './components/Toast';
import Auth from './components/Auth';
import {
  getCurrentUser,
  logout,
  changePassword,
  deleteAccount,
  getUserDetails,
  generateTwoFactorSecret,
  enableTwoFactor,
  disableTwoFactor,
  saveGeminiApiKey,
  removeGeminiApiKey,
  refreshGeminiKeyStatus,
  testGeminiApiKey,
  storeCurrentUser,
} from './services/authService';
import { deleteUserData } from './services/storageService';
import AccountSettingsModal from './components/AccountSettingsModal';
import AppModals from './components/AppModals';
import MainContent from './components/MainContent';
import useUserData from './src/hooks/useUserData';
import useSidebar from './src/hooks/useSidebar';
import useTheme from './src/hooks/useTheme';
import useFolderSelection from './src/hooks/useFolderSelection';
import usePromptActions from './src/hooks/usePromptActions';
import { PromptProvider, usePromptContext } from './src/context/PromptContext';
import {
  SIDEBAR_DESKTOP_COLLAPSED_WIDTH,
  SIDEBAR_DESKTOP_EXPANDED_WIDTH,
  SIDEBAR_MOBILE_TRANSITION,
  SIDEBAR_MOBILE_WIDTH,
  SIDEBAR_TRANSITION,
} from './layoutConstants';
import { cn } from './utils/cn';

const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const StatsModal = lazy(() => import('./components/StatsModal'));
const KeyboardShortcutsModal = lazy(() => import('./components/KeyboardShortcutsModal'));

export type Theme = 'light' | 'dark';

type ToastContextType = { showToast: (message: string, type?: 'success' | 'error', onUndo?: () => void) => void };
export const ToastContext = createContext<ToastContextType>({ showToast: () => {} });

const App: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const [currentUser, setCurrentUser] = useState<User | null>(() => getCurrentUser());
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastIdCounter = useRef(0);
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success', onUndo?: () => void) => {
    const id = `toast-${Date.now()}-${toastIdCounter.current++}`;
    const newToast: ToastItem = { id, message, type, onUndo };
    setToasts(prev => [...prev.slice(-4), newToast]); // keep max 5, show last 3
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const sidebar = useSidebar();
  const selection = useFolderSelection(sidebar.closeOnMobile);

  const { fileSystem, setFileSystem, templates, setTemplates, settings, setSettings, syncStatus, forceImmediateSync, refreshData } = useUserData(
    currentUser,
    showToast,
    () => selection.setSelectedSpecialView('all')
  );

  const handleLoginSuccess = (user: User) => setCurrentUser(user);

  const handleUpdateCurrentUser = useCallback((updates: Partial<User>) => {
    setCurrentUser(prev => {
      if (!prev) return prev;
      const nextUser = { ...prev, ...updates };
      storeCurrentUser(nextUser);
      return nextUser;
    });
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    setCurrentUser(null);
    setFileSystem([]);
    setTemplates([]);
    selection.setSelectedFolderId(null);
    selection.setSelectedPromptId(null);
    selection.clearPromptSelection();
    selection.setSelectedSpecialView('all');
    selection.setKindFilter('all');
    selection.setIsTemplateManagerOpen(false);
    selection.setIsPromptGeneratorOpen(false);
    selection.setActiveTag(null);
    selection.setSearchTerm('');
    showToast('Erfolgreich abgemeldet.');
  }, [showToast, selection, setFileSystem, setTemplates]);

  useEffect(() => {
    if (syncStatus === 'unauthorized') {
      handleLogout();
    }
  }, [syncStatus, handleLogout]);

  const handleChangePassword = async (currentPassword: string, newPassword: string) => {
    if (!currentUser) return { success: false, message: 'Nicht angemeldet.' };
    return await changePassword(currentPassword, newPassword);
  };

  const handleDeleteAccount = async (password: string) => {
    if (!currentUser) return { success: false, message: 'Nicht angemeldet.' };
    const result = await deleteAccount(password);
    if (result.success) {
      deleteUserData(currentUser.email);
      handleLogout();
    }
    return result;
  };

  return (
    <ErrorBoundary>
      <ToastContext.Provider value={{ showToast }}>
        <PromptProvider
          initialState={{
            fileSystem: fileSystem || [],
            setFileSystem,
            templates: templates || [],
            setTemplates,
            selectedFolderId: selection.selectedFolderId,
            setSelectedFolderId: selection.setSelectedFolderId,
            selectedPromptId: selection.selectedPromptId,
            setSelectedPromptId: selection.setSelectedPromptId,
            activeTag: selection.activeTag,
            setActiveTag: selection.setActiveTag,
            selectedSpecialView: selection.selectedSpecialView,
            setSelectedSpecialView: selection.setSelectedSpecialView,
            searchTerm: selection.searchTerm,
            setSearchTerm: selection.setSearchTerm,
            settings: settings || { globalSystemInstruction: '', promptGeneratorSystemPrompt: '', promptOptimizerSystemPrompt: '' },
            setSettings,
            showToast,
            forceImmediateSync,
            closeSidebar: sidebar.closeOnMobile,
            kindFilter: selection.kindFilter,
            setKindFilter: selection.setKindFilter,
          }}
        >
          {currentUser ? (
            <AppContent
              currentUser={currentUser}
              theme={theme}
              toggleTheme={toggleTheme}
              handleLogout={handleLogout}
              handleChangePassword={handleChangePassword}
              handleDeleteAccount={handleDeleteAccount}
              handleUpdateCurrentUser={handleUpdateCurrentUser}
              toasts={toasts}
              dismissToast={dismissToast}
              sidebar={sidebar}
              selection={selection}
              refreshData={refreshData}
              syncStatus={syncStatus}
            />
          ) : (
            <Auth
              onLoginSuccess={handleLoginSuccess}
              theme={theme}
              onToggleTheme={toggleTheme}
              showToast={showToast}
            />
          )}
        </PromptProvider>
      </ToastContext.Provider>
    </ErrorBoundary>
  );
};

const AppContent: React.FC<{
  currentUser: User;
  theme: Theme;
  toggleTheme: () => void;
  handleLogout: () => void;
  handleChangePassword: (cur: string, next: string) => Promise<{ success: boolean; message: string }>;
  handleDeleteAccount: (pwd: string) => Promise<{ success: boolean; message: string }>;
  handleUpdateCurrentUser: (updates: Partial<User>) => void;
  toasts: ToastItem[];
  dismissToast: (id: string) => void;
  sidebar: any;
  selection: any;
  refreshData: () => Promise<void>;
  syncStatus: string;
}> = ({
  currentUser,
  theme,
  toggleTheme,
  handleLogout,
  handleChangePassword,
  handleDeleteAccount,
  handleUpdateCurrentUser,
  toasts,
  dismissToast,
  sidebar,
  selection,
  refreshData,
  syncStatus,
}) => {
  const actions = usePromptActions();
  const { showToast, fileSystem, settings, setSettings } = usePromptContext();

  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false);
  const [isAdminDashboardOpen, setIsAdminDashboardOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        if (actions.canUndo && !isInput) {
          e.preventDefault();
          actions.handleUndo();
        }
      }
      if (isInput) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        actions.handleNewPromptRequest();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'E') {
        e.preventDefault();
        actions.handleExport();
      }
      if (e.key === '?') {
        // Only open if NOT in any modal/input
        if (!actions.modalState && !isAccountSettingsOpen && !isAdminDashboardOpen && !isStatsOpen) {
          e.preventDefault();
          setIsShortcutsOpen(prev => !prev);
        }
      }
      if (e.key === 'Escape') {
        if (isShortcutsOpen) {
          setIsShortcutsOpen(false);
        } else if (actions.modalState) {
          actions.setModalState(null);
        } else if (actions.contextMenu) {
          actions.setContextMenu(null);
        } else if (isAccountSettingsOpen) {
          setIsAccountSettingsOpen(false);
        } else if (isAdminDashboardOpen) {
          setIsAdminDashboardOpen(false);
        } else if (isStatsOpen) {
          setIsStatsOpen(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [actions, showToast, isAccountSettingsOpen, isAdminDashboardOpen, isStatsOpen, isShortcutsOpen]);

  const sidebarContainerClassName = sidebar.isMobile
    ? cn(
        'fixed inset-y-0 left-0 z-50',
        SIDEBAR_MOBILE_WIDTH,
        SIDEBAR_MOBILE_TRANSITION,
        sidebar.isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )
    : cn(
        'relative',
        SIDEBAR_TRANSITION,
        sidebar.isDesktopSidebarCollapsed
          ? SIDEBAR_DESKTOP_COLLAPSED_WIDTH
          : SIDEBAR_DESKTOP_EXPANDED_WIDTH
      );

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <div className="flex h-screen overflow-hidden bg-paper text-ink font-sans">
        {/* Mobile Sidebar Overlay */}
        {sidebar.isMobile && sidebar.isSidebarOpen && (
          <div 
            className="fixed inset-0 z-40 bg-black/40 transition-opacity duration-300"
            onClick={sidebar.toggleSidebar}
            aria-hidden="true"
          />
        )}
        
        {/* Sidebar Container */}
        <aside
          className={sidebarContainerClassName}
          aria-label="Hauptnavigation"
        >
          <Sidebar
            isCollapsed={sidebar.isDesktopSidebarCollapsed}
            onToggleCollapse={sidebar.toggleSidebar}
            isMobile={sidebar.isMobile}
          />
        </aside>

        {/* Main Content */}
        <main id="main-content" className="flex flex-col flex-1 overflow-hidden min-w-0">
          <Header
            theme={theme}
            onToggleTheme={toggleTheme}
            onLogout={handleLogout}
            onOpenAccountSettings={() => setIsAccountSettingsOpen(true)}
            onOpenAdminDashboard={() => setIsAdminDashboardOpen(true)}
            onOpenStats={() => setIsStatsOpen(true)}
            onOpenShortcuts={() => setIsShortcutsOpen(true)}
            isSidebarOpen={sidebar.isSidebarOpen}
            toggleSidebar={sidebar.toggleSidebar}
            isMobile={sidebar.isMobile}
            syncStatus={syncStatus}
            isAdmin={currentUser?.role === 'admin'}
            userEmail={currentUser?.email}
            searchTerm={selection.searchTerm}
            onSearch={selection.setSearchTerm}
            onNew={(kind) => actions.handleNewPromptRequest(undefined, kind)}
            onImportSkill={actions.handleImportSkill}
          />
          <div className="flex-1 overflow-hidden">
            <MainContent actions={actions} selection={selection} sidebar={sidebar} />
          </div>
        </main>

        {/* Overlays */}
        {actions.contextMenu && <ContextMenu {...actions.contextMenu} onClose={() => actions.setContextMenu(null)} />}
        <Toast toasts={toasts} onDismiss={dismissToast} />

        {isShortcutsOpen && (
          <Suspense fallback={null}>
            <KeyboardShortcutsModal onClose={() => setIsShortcutsOpen(false)} />
          </Suspense>
        )}

        {isStatsOpen && (
          <Suspense fallback={null}>
            <StatsModal
              fileSystem={fileSystem}
              allPrompts={actions.allPrompts}
              onClose={() => setIsStatsOpen(false)}
            />
          </Suspense>
        )}

        {isAccountSettingsOpen && (
          <AccountSettingsModal
            user={currentUser}
            onClose={() => setIsAccountSettingsOpen(false)}
            onChangePassword={handleChangePassword}
            onDeleteAccount={handleDeleteAccount}
            onGetUserDetails={getUserDetails}
            onGenerate2FA={generateTwoFactorSecret}
            onEnable2FA={enableTwoFactor}
            onDisable2FA={disableTwoFactor}
            onSaveGeminiKey={saveGeminiApiKey}
            onRemoveGeminiKey={removeGeminiApiKey}
            onTestGeminiKey={testGeminiApiKey}
            onGetGeminiKeys={refreshGeminiKeyStatus}
            onUpdateUser={handleUpdateCurrentUser}
            settings={settings}
            onUpdateSettings={(next) => setSettings(prev => ({ ...(prev || {}), ...next }))}
          />
        )}

        <AppModals actions={actions} />

        {isAdminDashboardOpen && currentUser?.role === 'admin' && (
          <Suspense
            fallback={
              <div className="modal-overlay">
                <div className="modal-content !w-auto px-6 py-4 text-sm text-graphite">
                  Verwaltung wird geladen …
                </div>
              </div>
            }
          >
            <AdminDashboard 
              currentUser={currentUser} 
              onClose={() => setIsAdminDashboardOpen(false)} 
              onLocalDataUpdate={refreshData}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default App;
